import { toast } from 'sonner';
import { useFragment, useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { EscrowMembershipRemoveDocument } from 'src/graphql/actions/escrowMemberships/escrowMembershipRemove.mutation';
import { EscrowMembershipFragment } from 'src/graphql/actions/escrowMemberships/escrowMemberships.fragment';
import { type IEscrowMembership } from 'src/graphql/schema';
import { Button } from '@/components/ui/button';

type Props = {
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  successCallback?: () => void;
};

const RemoveMembershipDialog = ({ id, open, onOpenChange, successCallback }: Props) => {
  const [removeEscrowMembership, { loading }] = useMutation(EscrowMembershipRemoveDocument);
  const membership = useFragment<IEscrowMembership>({
    fragment: EscrowMembershipFragment,
    from: { __typename: 'EscrowMembership', id },
  });
  const userName = membership?.data.appSubscription?.user?.name;

  const onRemove = async () => {
    try {
      await removeEscrowMembership({
        variables: {
          escrowMembershipId: id,
        },
      });

      onOpenChange(false);
      toast.success(`${userName} removed from this escrow`);
      successCallback?.();
    } catch {
      toast.error('Failed to remove member access');
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {userName} from this escrow?</AlertDialogTitle>
          <AlertDialogDescription>
            They&apos;ll immediately lose access to this escrow. You can add them again later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onRemove} disabled={loading} isLoading={loading}>
            Remove member
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveMembershipDialog;
